import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { AlertTriangle, Network } from 'lucide-react'
import { getAnalyticsConfig } from '@/lib/utils/analytics-status'
import { describeStatusPair, hierarchyDiffers, hierarchyLabel, rollupCode, rollupIsAmber, type StatusPair } from '@/lib/utils/object-status-pair'

/**
 * An object's own scan and its rolled-up hierarchy status, shown as two things — never blended.
 *
 * TWO DIFFERENT MARKS, so they cannot be mistaken for each other (Andrew, 12 Oct 2026: "We are
 * using two circles ... The hierarchy should not be a circle, use the network icon and have it
 * in red, amber or green"):
 *   the DOT            is the object's own scan;
 *   the NETWORK ICON   is the hierarchy - the object and everything it depends on - in the
 *                      roll-up's colour.
 *
 *   ring      — both marks side by side, dot then icon, plus the counts. (Named for the ring the
 *               hierarchy used to be drawn as, round the dot.) A green dot beside an amber icon
 *               says "this one is fine, something beneath it is not".
 *
 * The hierarchy mark is only ever green, amber or grey — red is the object's own scan, and a
 * confirmed detection in the stack shows as amber with the red count beside it. Indicators
 * beneath never change the colour, so they are flagged with an amber triangle instead; when the
 * roll-up is already amber the triangle is dropped rather than repeat the colour.
 *   chips     — the object page: "Scan ● …" and, when it differs, "Hierarchy ● …" naming the
 *               red dependency, with the indicators beneath as a quiet line of their own.
 *   scan      — table column: the object's own scan alone.
 *   hierarchy — table column: the roll-up, with the counts that explain it.
 *
 * Counts beside a mark: red = dependencies beneath with confirmed detections (the only thing
 * that rolls up); amber = dependencies with indicators, which do not change the colour.
 */
interface ObjectStatusPairProps extends StatusPair {
    variant?: 'ring' | 'chips' | 'scan' | 'hierarchy'
    size?: string
}

function Dot({ code, size }: { code: number | null | undefined; size: string }) {
    const cfg = getAnalyticsConfig(code == null ? null : Number(code))
    return <span className={`inline-block shrink-0 rounded-full ${size} ${cfg.color}`} data-testid="status-scan-dot" />
}

/**
 * The hierarchy's mark: the network icon, in the roll-up's colour (the same shade the dots use).
 * A heavier stroke than the icon's default, so the colour reads at table size.
 */
function HierarchyMark({ code, size }: { code: number | null | undefined; size: string }) {
    const cfg = getAnalyticsConfig(code == null ? null : Number(code))
    return <Network className={`shrink-0 ${size}`} style={{ color: cfg.solid }} strokeWidth={2.5} aria-hidden="true" data-testid="status-hierarchy-icon" />
}

/**
 * What sits beneath, beside the mark: the red count (the reason an amber roll-up is amber) and,
 * where indicators beneath left the colour alone, an amber triangle. `amber` says the roll-up is
 * already amber, in which case the triangle would add nothing.
 */
function Counts({ red, ind, amber }: { red: number; ind: number; amber: boolean }) {
    const flag = ind > 0 && !amber
    if (red <= 0 && !flag) return null
    return (
        <span className="inline-flex items-center gap-0.5">
            {red > 0 && <span className="min-w-[1rem] rounded-full bg-red-600 px-1 text-center text-[10px] font-bold leading-4 text-white tabular-nums">{red}</span>}
            {flag && <AlertTriangle className="h-3.5 w-3.5 shrink-0 text-amber-500" />}
        </span>
    )
}

export function ObjectStatusPair({ variant = 'ring', size = 'h-3 w-3', ...pair }: ObjectStatusPairProps) {
    const { t } = useTranslation()
    const title = describeStatusPair(pair, t as any).join('\n')
    const red = Number(pair.redBelow || 0)
    const ind = Number(pair.indicatorsBelow || 0)
    const rollup = rollupCode(pair.hierarchy)
    const amber = rollupIsAmber(pair)

    if (variant === 'scan') {
        return (
            <span className="inline-flex items-center" title={title} aria-label={title}>
                <Dot code={pair.scan} size={size} />
            </span>
        )
    }

    if (variant === 'hierarchy') {
        return (
            <span className="inline-flex items-center gap-1.5" title={title} aria-label={title}>
                <HierarchyMark code={rollup} size={size} />
                <Counts red={red} ind={ind} amber={amber} />
            </span>
        )
    }

    if (variant === 'ring') {
        return (
            <span className="inline-flex items-center gap-1.5" title={title} aria-label={title}>
                <Dot code={pair.scan} size={size} />
                <HierarchyMark code={rollup} size={size} />
                <Counts red={red} ind={ind} amber={amber} />
            </span>
        )
    }

    // chips — full words, for the object page.
    const scanCfg = getAnalyticsConfig(pair.scan == null ? null : Number(pair.scan))
    const differs = hierarchyDiffers(pair)
    const ids = (list: string[] | null | undefined) => (list || []).slice(0, 3)
    return (
        <div className="flex flex-wrap items-center gap-2 text-xs" aria-label={title}>
            <span className="inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5"
                  title={t('objects.statusPair.scanTitle', "This object's own latest scan")}>
                <span className="text-muted-foreground">{t('objects.statusPair.scan', 'Scan')}</span>
                <Dot code={pair.scan} size="h-2.5 w-2.5" />
                <span className="font-medium">{scanCfg.label}</span>
            </span>
            {(differs || red > 0) && (
                <span className="inline-flex flex-wrap items-center gap-1.5 rounded-full border px-2 py-0.5"
                      title={t('objects.statusPair.hierarchyTitle', 'Including everything it depends on. A red dependency makes this amber; amber dependencies do not roll up.')}>
                    <span className="text-muted-foreground">{t('objects.statusPair.hierarchy', 'Hierarchy')}</span>
                    <HierarchyMark code={rollup} size="h-3.5 w-3.5" />
                    <span className="font-medium">{hierarchyLabel(pair, t as any)}</span>
                    {red > 0 && (
                        <span className="text-muted-foreground">
                            — {t('objects.statusPair.redBelow', 'Confirmed detections beneath: {{count}}', { count: red })}{' '}
                            {ids(pair.redBelowIds).map((id, i) => (
                                <span key={id}>{i > 0 && ', '}<Link to={`/objects/${encodeURIComponent(id)}`} className="font-medium text-red-700 hover:underline dark:text-red-400">{id}</Link></span>
                            ))}
                        </span>
                    )}
                </span>
            )}
            {ind > 0 && (
                <span className="inline-flex items-center gap-1 text-amber-700 dark:text-amber-400" title={(pair.indicatorsBelowIds || []).slice(0, 10).join(', ')}>
                    <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
                    {t('objects.statusPair.indicatorsBelow', 'Indicators beneath (not detections): {{count}}', { count: ind })}
                </span>
            )}
        </div>
    )
}
